import { useId, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { TIMING_TONE, WHEN_LABELS } from '../api/v2ReportApi';
import type { V2AdvisorActions, V2AdvisorNeed } from '../api/v2AdvisorReportApi';
import { SafeLink, SupportItem } from './SupportItems';

const EYEBROW = 'text-[11px] font-semibold uppercase tracking-wider text-[#026857]';
const CHIP = 'rounded-full px-2.5 py-0.5 text-xs font-medium';

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className={EYEBROW}>{label}</div>
      {children}
    </div>
  );
}

/** What the client's own report gave them: Start Here, then who and what it points to. */
function Actions({ actions }: { actions: V2AdvisorActions }) {
  const { startHere, workbook, referredTo, then } = actions;
  return (
    <Section label="Actions for your client">
      <p className="text-[14px] leading-relaxed text-gray-800">{startHere}</p>
      {(workbook || referredTo || then.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 pt-0.5 text-xs">
          {workbook && (
            <span className={`${CHIP} bg-[#efeae0] text-gray-700`}>
              Workbook:{' '}
              <SafeLink href={workbook.href} fallback={workbook.label}>
                {workbook.label}
              </SafeLink>
            </span>
          )}
          {referredTo && (
            <span className={`${CHIP} bg-[#eaf1ee] text-[#026857]`}>
              Referred to: <SupportItem item={referredTo} />
            </span>
          )}
          {then.length > 0 && (
            <span className="text-gray-500">
              Then:{' '}
              {then.map((item, i) => (
                <span key={`${item.label}-${i}`}>
                  {i > 0 && '; '}
                  <SupportItem item={item} />
                </span>
              ))}
            </span>
          )}
        </div>
      )}
    </Section>
  );
}

/**
 * One of the client's top 3 on the advisor report: a row (rank, title, timing,
 * pillar · readiness) that opens to three sections in Kelly's order — Why it
 * matters, Actions for your client, Conversation starters with your client.
 * Draws the payload only; the conversation starter is the matrix's copy as
 * sent, so no quotation marks are added.
 */
export function V2AdvisorNeedCard({
  need,
  defaultOpen = false,
}: {
  need: V2AdvisorNeed;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  return (
    <article className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-start gap-4 px-5 py-4 text-left hover:bg-[#fbf9f5]"
      >
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f6e7c4] font-serif text-[15px] font-semibold text-[#04332b]"
          aria-label={`Priority ${need.rank}`}
        >
          {need.rank}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-serif text-[17px] font-semibold text-gray-900">{need.title}</span>
            <span
              className="whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium"
              style={TIMING_TONE[need.when]}
            >
              {WHEN_LABELS[need.when]}
            </span>
          </span>
          <span className="mt-0.5 block text-[13px] text-gray-500">
            {need.pillarName} · {need.readinessLabel}
          </span>
        </span>
        <ChevronDown
          className={`mt-2 h-4 w-4 shrink-0 text-gray-500 transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {open && (
        <div id={panelId} className="space-y-4 border-t border-gray-100 px-5 py-4 sm:pl-[68px]">
          <Section label="Why it matters">
            <p className="text-[14px] leading-relaxed text-gray-800">{need.whyItMatters}</p>
          </Section>
          <div className="border-t border-[#ece6d9] pt-4">
            <Actions actions={need.actions} />
          </div>
          <div className="rounded-xl bg-[#eaf1ee] px-4 py-3.5">
            <Section label="Conversation starters with your client">
              <p className="font-serif text-[15px] italic leading-relaxed text-gray-800">
                {need.conversationStarter}
              </p>
            </Section>
          </div>
        </div>
      )}
    </article>
  );
}
